import {Box, Grid, Section, Statistic} from '@primer/react-brand/esm'
import type {ReactElement} from 'react'
import {FlexSuiteIntroIconCards, FlexSuiteIntroImageCards} from '../../../components/FlexSuiteIntroCards'
import {FlexSuiteIntroLogos} from '../../../components/FlexSuiteIntroLogos'
import {FlexSuiteIntroPillars} from '../../../components/FlexSuiteIntroPillars'
import {FlexSuiteIntroSimple} from '../../../components/FlexSuiteIntroSimple'
import {hasIntroSemanticHeading, renderWithoutParagraphs} from '../../../utils'
import type {IntroSectionProps} from './types'

type IntroContextContentProps = Pick<
  IntroSectionProps,
  | 'introContext'
  | 'introContextHeading'
  | 'introContextLink'
  | 'introContextBody'
  | 'introItems'
  | 'introIconCards'
  | 'introPillars'
  | 'introImageCards'
  | 'introStats'
> & {
  promoteFirstHeading: boolean
  hasImageCardsTopBorder: boolean
}

export function IntroSection({
  hasPriorSemanticH2 = false,
  introLogoHeading,
  introLogos,
  introLogoLink,
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading = true,
  hasImageCardsTopBorder = true,
}: IntroSectionProps) {
  const promoteFirstHeading =
    !hasPriorSemanticH2 &&
    !hasIntroSemanticHeading({
      hasLogoHeading: Boolean(introLogos?.length),
      hasIntroContextHeading: Boolean(introContextHeading),
    })
  const introContextContent = renderIntroContextContent({
    introContext,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconCards,
    introPillars,
    introImageCards,
    introStats,
    promoteFirstHeading,
    hasImageCardsTopBorder: hasImageCardsTopBorder && !introContextHeading && !introLogos?.length,
  })

  const hasIntroContent = Boolean(introLogos?.length) || introContextContent !== null

  if (!hasIntroContent) return null

  const takeoverButton =
    logobarOptions?.takeoverButton ??
    (introLogoLink?.fields.href ? {label: introLogoLink.fields.text, href: introLogoLink.fields.href} : undefined)

  return (
    <Section fullWidth paddingBlockEnd="none" paddingBlockStart="none">
      <FlexSuiteIntroLogos
        heading={introLogoHeading}
        logos={introLogos}
        logobarOptions={{...logobarOptions, takeoverButton}}
        logoSuiteOptions={logoSuiteOptions}
        hideHeading={hideLogoHeading}
      />
      {introContextContent}
    </Section>
  )
}

function renderIntroContextContent({
  introContext,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  promoteFirstHeading,
  hasImageCardsTopBorder,
}: IntroContextContentProps): ReactElement | null {
  if (!introContext) return null

  switch (introContext) {
    case 'Simple Intro':
      if (!introContextHeading && !introContextBody) return null
      return (
        <Box marginBlockStart={{narrow: 48, regular: 80}} marginBlockEnd={{narrow: 48, regular: 80}}>
          <Grid>
            <Grid.Column>
              <FlexSuiteIntroSimple
                heading={introContextHeading}
                description={introContextBody}
                link={introContextLink}
                items={introItems}
                promoteFirstHeading={promoteFirstHeading}
              />
            </Grid.Column>
          </Grid>
        </Box>
      )
    case 'Pillars': {
      if (!introPillars || introPillars.length === 0) return null
      return <FlexSuiteIntroPillars introPillars={introPillars} promoteFirstHeading={promoteFirstHeading} />
    }
    case 'Icon Cards':
      if (!introIconCards || introIconCards.length === 0) return null
      return <FlexSuiteIntroIconCards cards={introIconCards} promoteFirstHeading={promoteFirstHeading} />
    case 'Image Cards':
      if (!introImageCards || introImageCards.length === 0) return null
      return (
        <FlexSuiteIntroImageCards
          cards={introImageCards}
          promoteFirstHeading={promoteFirstHeading}
          hasTopBorder={hasImageCardsTopBorder}
        />
      )
    case 'Stats':
      if (!introStats || introStats.length === 0) return null
      return (
        <Box marginBlockStart={{narrow: 48, regular: 80}} marginBlockEnd={{narrow: 48, regular: 80}}>
          <Grid>
            {introStats.map(stat => (
              <Grid.Column
                key={stat.sys.id}
                span={{medium: introStats.length === 4 ? 6 : 4, large: introStats.length === 4 ? 3 : 4}}
              >
                <Statistic>
                  <Statistic.Heading>{renderWithoutParagraphs(stat.fields.heading)}</Statistic.Heading>
                  <Statistic.Description>{renderWithoutParagraphs(stat.fields.description)}</Statistic.Description>
                </Statistic>
              </Grid.Column>
            ))}
          </Grid>
        </Box>
      )
    default:
      return null
  }
}
